"use client"

/**
 * Web Demo Settings Panel
 *
 * Appearance, demo guardrails (rate limits, allowed origins, skip
 * validation), and installation / token management for `web_demo`
 * channels. Mirrors the `web_widget` layout so the two panels look the
 * same except for channel-specific controls:
 *   - `web_demo` has rate limits, origin allowlist, skip-validation.
 *   - `web_demo` omits bubble / window placement controls (inline only).
 *
 * Shared pieces (appearance fields, avatar upload, regenerate-token
 * confirm dialog) live in sibling components and are imported by both
 * panels — see `widget-appearance-fields.tsx`,
 * `avatar-upload-control.tsx`, `regenerate-token-button.tsx`.
 */

import { useCallback, useEffect, useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Loader2, Save, Copy, Check, Plus, X, Eye } from "lucide-react"
import { toast } from "sonner"
import { useAuth } from "@/hooks/auth/use-auth"
import type { MessageAgentChannel } from "@/hooks/agents/messaging/use-agents"
import type { WebDemoChannelConfig, WebWidgetConfig } from "@/lib/agents/messaging/types"
import { WEB_DEMO_RATE_LIMIT_DEFAULTS } from "@/lib/agents/messaging/types"
import { WIDGET_DEFAULT_CONFIG } from "@/lib/agents/messaging/channels/web-widget/constants"
import { buildWidgetEmbedSnippet } from "@/lib/agents/messaging/channels/web-widget/embed-snippet"
import { WidgetAppearanceFields } from "./widget-appearance-fields"
import { AvatarUploadControl } from "./avatar-upload-control"
import { RegenerateTokenButton } from "./regenerate-token-button"
import { WidgetPreview } from "./widget-preview"
import { useTranslations } from "next-intl"


interface WebDemoSettingsProps {
  channel: MessageAgentChannel
  onRefresh: () => void
  canManage: boolean
}

export function WebDemoSettings({ channel, onRefresh, canManage }: WebDemoSettingsProps) {
  const tUi = useTranslations("Messaging.AgentsMessagingSettingsWebDemoSettings")

  const { fetchWithAuth } = useAuth()
  const channelConfig = (channel.config ?? {}) as Partial<WebDemoChannelConfig>

  const [config, setConfig] = useState<WebWidgetConfig>({
    ...WIDGET_DEFAULT_CONFIG,
    ...channelConfig,
  })
  const [skipValidation, setSkipValidation] = useState(channelConfig.skip_domain_validation ?? false)
  const [origins, setOrigins] = useState<string[]>(channelConfig.allowed_origins ?? [])
  const [newOrigin, setNewOrigin] = useState("")
  const [ipLimit, setIpLimit] = useState(
    channelConfig.rate_limits?.ip_sessions_per_hour ?? WEB_DEMO_RATE_LIMIT_DEFAULTS.ip_sessions_per_hour,
  )
  const [msgConvLimit, setMsgConvLimit] = useState(
    channelConfig.rate_limits?.messages_per_minute_conversation
      ?? WEB_DEMO_RATE_LIMIT_DEFAULTS.messages_per_minute_conversation,
  )
  const [msgChannelLimit, setMsgChannelLimit] = useState(
    channelConfig.rate_limits?.messages_per_minute_channel
      ?? WEB_DEMO_RATE_LIMIT_DEFAULTS.messages_per_minute_channel,
  )

  /**
   * Number inputs emit "" when cleared and "NaN" for malformed entries like
   * "1e". Clamp to the input's [min, max] — keeps the field usable without
   * yo-yoing to a default on every keystroke.
   */
  const parseIntInRange = (raw: string, min: number, max: number, fallback: number): number => {
    const n = Number(raw)
    if (!Number.isFinite(n)) return fallback
    const int = Math.trunc(n)
    if (int < min) return min
    if (int > max) return max
    return int
  }

  const [saving, setSaving] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [token, setToken] = useState(channel.external_account_id ?? "")

  useEffect(() => {
    setToken(channel.external_account_id ?? "")
  }, [channel.external_account_id])

  const host = typeof window !== "undefined" ? window.location.origin : ""
  const embedSnippet = buildWidgetEmbedSnippet(host, { token, inlineContainer: "#chat-root" })

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(embedSnippet)
      setCopied(true)
      toast.success(tUi("copiedToClipboard"))
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error(tUi("failedToCopy"))
    }
  }, [embedSnippet, tUi])

  const handleAddOrigin = useCallback(() => {
    const trimmed = newOrigin.trim()
    if (!trimmed) return
    let normalized = trimmed
    if (trimmed !== "*") {
      try {
        normalized = new URL(trimmed).origin
      } catch {
        toast.error(tUi("invalidURLMustBeAnOriginLikeHttps"))
        return
      }
    }
    if (origins.includes(normalized)) {
      toast.error(tUi("originAlreadyInList"))
      return
    }
    setOrigins([...origins, normalized])
    setNewOrigin("")
  }, [newOrigin, origins, tUi])

  const handleRemoveOrigin = useCallback(
    (idx: number) => setOrigins(origins.filter((_, i) => i !== idx)),
    [origins],
  )

  const handleSave = useCallback(async () => {
    setError(null)
    setSaving(true)
    try {
      const payload: WebDemoChannelConfig = {
        primaryColor: config.primaryColor,
        welcomeMessage: config.welcomeMessage,
        title: config.title,
        avatarUrl: config.avatarUrl,
        showBranding: config.showBranding,
        borderRadius: config.borderRadius,
        headerStyle: config.headerStyle,
        headerSubtitle: config.headerSubtitle,
        fontFamily: config.fontFamily,
        fontSize: config.fontSize,
        brandingText: config.brandingText,
        typingIndicatorDelay: config.typingIndicatorDelay,
        siteUrl: config.siteUrl,
        skip_domain_validation: skipValidation,
        allowed_origins: origins,
        rate_limits: {
          ip_sessions_per_hour: ipLimit,
          messages_per_minute_conversation: msgConvLimit,
          messages_per_minute_channel: msgChannelLimit,
        },
      }

      const response = await fetchWithAuth("/api/agents/messaging/channels/web-demo/config", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ channelId: channel.id, config: payload }),
      })

      if (!response.ok) {
        const data = await response.json()
        setError(data.error || "Failed to save config")
        return
      }

      toast.success(tUi("demoWidgetConfigSaved"), {
        description: tUi("changesMayTakeUpTo15MinutesTo"),
      })
      onRefresh()
    } catch {
      setError(tUi("failedToSaveConfig"))
    } finally {
      setSaving(false)
    }
  }, [config.primaryColor, config.welcomeMessage, config.title, config.avatarUrl, config.showBranding, config.borderRadius, config.headerStyle, config.headerSubtitle, config.fontFamily, config.fontSize, config.brandingText, config.typingIndicatorDelay, config.siteUrl, skipValidation, origins, ipLimit, msgConvLimit, msgChannelLimit, fetchWithAuth, channel.id, tUi, onRefresh])

  const handleRegenerate = useCallback(async () => {
    setError(null)
    setRegenerating(true)
    try {
      const response = await fetchWithAuth("/api/agents/messaging/channels/web-demo/regenerate-token", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ channelId: channel.id }),
      })

      if (!response.ok) {
        const data = await response.json()
        setError(data.error || "Failed to regenerate token")
        return
      }

      const data = await response.json()
      setToken(data.token)
      toast.success(tUi("tokenRegeneratedUpdateTheEmbedCodeOnYour"))
      onRefresh()
    } catch {
      setError(tUi("failedToRegenerateToken"))
    } finally {
      setRegenerating(false)
    }
  }, [fetchWithAuth, channel.id, tUi, onRefresh])

  const avatarControl = (
    <AvatarUploadControl
      avatarUrl={config.avatarUrl}
      channelId={channel.id}
      uploadPath="/api/agents/messaging/channels/web-demo/avatar"
      canManage={canManage}
      onChange={(url) => {
        setConfig({ ...config, avatarUrl: url })
        // The avatar route persists config.avatarUrl immediately; refresh so
        // the parent channel object stays in sync instead of waiting for the
        // next Publish.
        if (url) onRefresh()
      }}
      onError={setError}
    />
  )

  return (
    <div className="flex gap-6">
      {/* Left column: settings — mirrors web-widget-settings layout. */}
      <div className="flex-1 min-w-0 space-y-6">
      {error && (
        <Alert className="border-red-200 bg-red-50">
          <AlertDescription className="text-red-800">{error}</AlertDescription>
        </Alert>
      )}

      <WidgetAppearanceFields
        config={config}
        onChange={setConfig}
        canManage={canManage}
        avatarControl={avatarControl}
        showBrandingToggle
      />

      {/* --- Demo-specific guardrails --- */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base"><span key={tUi("demoControls")}>{tUi("demoControls")}</span></CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="demo-site-url"><span key={tUi("websiteURL")}>{tUi("websiteURL")}</span></Label>
            <Input
              id="demo-site-url"
              type="url"
              placeholder={"https://landing.example.com"}
              value={config.siteUrl ?? ""}
              onChange={(e) => setConfig({ ...config, siteUrl: e.target.value })}
              disabled={!canManage}
              className="text-sm"
            />
            <p className="text-xs text-gray-500">
              <span key={tUi("theLandingPageWhereThisDemoWidgetIs")}>{tUi("theLandingPageWhereThisDemoWidgetIs")}</span></p>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label><span key={tUi("skipDomainValidation")}>{tUi("skipDomainValidation")}</span></Label>
              <p className="text-xs text-gray-500 mt-0.5">
                <span key={tUi("allowShopifyCustomDomainsAndSkipTheMCP")}>{tUi("allowShopifyCustomDomainsAndSkipTheMCP")}</span></p>
            </div>
            <Switch
              checked={skipValidation}
              onCheckedChange={setSkipValidation}
              disabled={!canManage}
            />
          </div>

          <div className="space-y-2">
            <Label><span key={tUi("allowedEmbedOrigins")}>{tUi("allowedEmbedOrigins")}</span></Label>
            <p className="text-xs text-gray-500">
              <span key={tUi("restrictWhichPagesCanEmbedThisWidgetChecked")}>{tUi("restrictWhichPagesCanEmbedThisWidgetChecked")}</span></p>
            {origins.map((origin, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <code className="flex-1 text-sm bg-gray-50 px-2 py-1 rounded border">{origin}</code>
                {canManage && (
                  <Button variant="ghost" size="sm" onClick={() => handleRemoveOrigin(idx)}>
                    <X className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            ))}
            {canManage && (
              <div className="flex gap-2">
                <Input
                  placeholder={"https://landing.example.com"}
                  value={newOrigin}
                  onChange={(e) => setNewOrigin(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddOrigin())}
                  className="text-sm"
                />
                <Button variant="outline" size="sm" onClick={handleAddOrigin} disabled={!newOrigin.trim()}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <Label><span key={tUi("rateLimits")}>{tUi("rateLimits")}</span></Label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
              <div className="space-y-1.5">
                <Label htmlFor="rl-ip" className="text-xs font-normal text-gray-500">
                  <span key={tUi("sessionsIPHour")}>{tUi("sessionsIPHour")}</span></Label>
                <Input
                  id="rl-ip"
                  type="number"
                  min={1}
                  max={100}
                  value={ipLimit}
                  onChange={(e) => setIpLimit(parseIntInRange(e.target.value, 1, 100, ipLimit))}
                  disabled={!canManage}
                  className="text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rl-conv" className="text-xs font-normal text-gray-500">
                  <span key={tUi("messagesConvMin")}>{tUi("messagesConvMin")}</span></Label>
                <Input
                  id="rl-conv"
                  type="number"
                  min={1}
                  max={200}
                  value={msgConvLimit}
                  onChange={(e) => setMsgConvLimit(parseIntInRange(e.target.value, 1, 200, msgConvLimit))}
                  disabled={!canManage}
                  className="text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rl-chan" className="text-xs font-normal text-gray-500">
                  <span key={tUi("messagesChannelMin")}>{tUi("messagesChannelMin")}</span></Label>
                <Input
                  id="rl-chan"
                  type="number"
                  min={1}
                  max={500}
                  value={msgChannelLimit}
                  onChange={(e) => setMsgChannelLimit(parseIntInRange(e.target.value, 1, 500, msgChannelLimit))}
                  disabled={!canManage}
                  className="text-sm"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* --- Publish --- */}
      {canManage && (
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            <span><span key={tUi("publishAppearance")}>{tUi("publishAppearance")}</span></span>
          </Button>
        </div>
      )}

      {/* --- Installation (bottom, matches web_widget layout) --- */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Eye className="w-4 h-4" />
            <span><span key={tUi("installation")}>{tUi("installation")}</span></span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-500">
            <span><span key={tUi("addThisCodeToYourLandingPageThe")}>{tUi("addThisCodeToYourLandingPageThe")}</span></span>{" "}
            <code className="bg-gray-100 px-1 rounded">#chat-root</code> <span><span key={tUi("container")}>{tUi("container")}</span></span>
          </p>
          <pre className="p-4 bg-gray-50 border rounded-lg text-sm whitespace-pre-wrap break-all overflow-x-auto font-mono leading-relaxed">
            {embedSnippet}
          </pre>
          <Button variant="outline" size="sm" onClick={handleCopy}>
            {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
            <span><span key={tUi("copyEmbedCode")}>{tUi("copyEmbedCode")}</span></span>
          </Button>
          <p className="text-xs text-gray-400">
            <span><span key={tUi("token")}>{tUi("token")}</span></span> <code className="bg-gray-100 px-1 rounded">{token}</code>
          </p>

          {canManage && (
            <div className="pt-2 border-t">
              <RegenerateTokenButton
                onConfirm={handleRegenerate}
                loading={regenerating}
                dialogDescription="All existing demo embeds will stop working immediately. You'll need to update the embed snippet on your landing page."
              />
            </div>
          )}
        </CardContent>
      </Card>
      </div>

      {/* Right column: live preview. Inline mode hides the bubble / offset
          block since demo widget has no floating launcher. */}
      <div className="hidden lg:flex lg:flex-col lg:items-center sticky top-0 self-start pt-2 pb-4 gap-4 max-h-screen overflow-y-auto">
        <WidgetPreview config={config} mode="inline" showOfflineNotice={false} />
      </div>
    </div>
  )
}
